import { useState, useEffect } from 'react';
import { parseTimeInput } from './timeFieldUtils';

/**
 * @param {string | undefined | null} value
 * @returns {string}
 */
const toDisplayValue = (value) => {
  const parsed = parseTimeInput(value || '');
  if (parsed === null) {
    return typeof value === 'string' ? value.trim() : '';
  }
  return parsed;
};

/**
 * @typedef {object} TimeFieldHookResult
 * @property {string} displayValue - Current display value
 * @property {boolean} isEmpty - Whether the field is empty
 * @property {(e: React.ChangeEvent<HTMLInputElement>) => void} handleChange - Change handler
 * @property {() => void} handleBlur - Blur handler
 * @property {(e: React.KeyboardEvent<HTMLInputElement>) => void} handleKeyDown - KeyDown handler
 * @property {(value: string) => void} selectOption - Select a quarter-hour option
 * @property {() => boolean} commitDisplayValue - Parse display value and notify parent; returns false if invalid
 */

/**
 * Hook for managing TimeField state and behavior
 * @param {string} value - Initial value in HH:mm format
 * @param {(value: string) => void} onChange - Change handler
 * @returns {TimeFieldHookResult} TimeField state and handlers
 */
export const useTimeField = (value, onChange) => {
  const [displayValue, setDisplayValue] = useState(() => toDisplayValue(value));

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setDisplayValue(toDisplayValue(value));
    }
  }, [value]);

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleChange = (e) => {
    const input = e.target.value;
    setDisplayValue(input);

    if (input.trim() === '') {
      onChange('');
    }
  };

  /**
   * @returns {boolean} True when commit succeeded
   */
  const commitDisplayValue = () => {
    const parsed = parseTimeInput(displayValue);

    if (parsed === null) {
      setDisplayValue(toDisplayValue(value));
      return false;
    }

    setDisplayValue(parsed);
    onChange(parsed);
    return true;
  };

  const handleBlur = () => {
    commitDisplayValue();
  };

  /**
   * @param {React.KeyboardEvent<HTMLInputElement>} e
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDisplayValue();
    }
  };

  /**
   * @param {string} optionValue - HH:mm value
   */
  const selectOption = (optionValue) => {
    const parsed = parseTimeInput(optionValue);
    if (parsed === null || parsed === '') {
      return;
    }

    setDisplayValue(parsed);
    onChange(parsed);
  };

  const isEmpty = !displayValue || displayValue === '';

  return {
    displayValue,
    isEmpty,
    handleChange,
    handleBlur,
    handleKeyDown,
    selectOption,
    commitDisplayValue,
  };
};
